import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";
import { Card, Badge, Form, InputGroup } from "react-bootstrap";
import { Button, Select, Tabs } from "antd";
import { QuestionCircleOutlined, MessageOutlined, CustomerServiceOutlined, PlusOutlined, ReloadOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

const { TabPane } = Tabs;

const normaliseResponse = (response) => {
    if (!response?.success) {
        return [];
    }
    if (Array.isArray(response?.data)) {
        return response.data;
    }
    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }
    return [];
};

const HelpSupport = () => {
    const { userData } = useAuth();
    const [activeTab, setActiveTab] = useState("faq");
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [showTicketForm, setShowTicketForm] = useState(false);
    const [submittingTicket, setSubmittingTicket] = useState(false);
    const [ticketForm, setTicketForm] = useState({
        subject: "",
        category: "general",
        priority: "medium",
        message: ""
    });
    const [expandedFaq, setExpandedFaq] = useState(null);

    const faqs = [
        {
            id: 1,
            question: "How do I file a new claim?",
            answer: "To file a new claim, navigate to the Claims page and click on 'File New Claim'. Fill in the required information including claim amount, incident date, and reason. Upload supporting documents such as medical reports, bills, or police reports. Once submitted, your claim will be reviewed by our team.",
            category: "Claims"
        },
        {
            id: 2,
            question: "How can I renew my policy?",
            answer: "You can renew your policy from the Policies page. Click on the policy you want to renew and follow the renewal process. Make sure to review your coverage details and update any information if needed. Payment can be made through the Payments section.",
            category: "Policies"
        },
        {
            id: 3,
            question: "Where can I view my payment history?",
            answer: "Your complete payment history is available in the Payments section. You can filter payments by status, payment method, and search by policy number. All invoices can be downloaded from the Payments page.",
            category: "Payments"
        },
        {
            id: 4,
            question: "How do I add or update family members/beneficiaries?",
            answer: "Go to Customer Management section and click on 'Add Family Member' button. Select the customer and fill in the family member details including name, relationship, date of birth, and contact information. Family members will be linked to the main customer account.",
            category: "Customers"
        },
        {
            id: 5,
            question: "What documents do I need to upload?",
            answer: "You can upload important documents in the Documents section. Supported file types include PDF, Word documents, Images, and Text files. Maximum file size is 10MB. Documents are categorized as Policies, Claims, Identification, or Other for easy organization.",
            category: "Documents"
        },
        {
            id: 6,
            question: "How do I track my claim status?",
            answer: "You can track your claim status in the Claims page. Claims are categorized as Pending, Under Review, Approved, or Rejected. Click on any claim to view detailed information including claim amount, approved amount, and status updates.",
            category: "Claims"
        },
        {
            id: 7,
            question: "Can I make payments online?",
            answer: "Yes, you can make payments online through the Payments section. We support various payment methods including online transfers, UPI, credit/debit cards, and other digital payment options. All payments are secure and encrypted.",
            category: "Payments"
        },
        {
            id: 8,
            question: "How do I update my profile information?",
            answer: "Go to Profile Settings and navigate through the tabs to update your personal details, contact information, default location, and gallery. Make sure to save your changes after updating any information.",
            category: "Profile"
        }
    ];

    useEffect(() => {
        if (activeTab === "tickets") {
            fetchTickets();
        }
    }, [activeTab, userData?.id]);

    const fetchTickets = async () => {
        if (!userData?.id) {
            setTickets([]);
            return;
        }

        try {
            setLoadingTickets(true);
            const conditions = { user_id: userData.id };
            
            const response = await oneKlicks.read(
                "",
                "insurance_support_tickets",
                conditions,
                "created_at DESC",
                "",
                "",
                "0,200"
            );

            const ticketRows = normaliseResponse(response);
            setTickets(ticketRows || []);
        } catch (error) {
            console.error("Error fetching tickets:", error);
            setTickets([]);
        } finally {
            setLoadingTickets(false);
        }
    };

    const handleTicketSubmit = async (e) => {
        e.preventDefault();
        
        if (!ticketForm.subject.trim()) {
            toast.error("Please enter a subject");
            return;
        }

        if (!ticketForm.message.trim()) {
            toast.error("Please enter a message");
            return;
        }

        setSubmittingTicket(true);
        try {
            const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
            
            const payload = {
                subject: ticketForm.subject.trim(),
                category: ticketForm.category,
                priority: ticketForm.priority,
                message: ticketForm.message.trim(),
                status: "open",
                user_id: userData?.id,
                company_id: userData?.company_id || null,
                agent_id: userData?.agent_id || null,
                created_at: timestamp,
                updated_at: timestamp
            };

            const response = await oneKlicks.create("", "insurance_support_tickets", payload);
            
            if (response?.success) {
                toast.success("Support ticket created successfully");
                setTicketForm({
                    subject: "",
                    category: "general",
                    priority: "medium",
                    message: ""
                });
                setShowTicketForm(false);
                fetchTickets();
            } else {
                toast.error("Failed to create support ticket");
            }
        } catch (error) {
            console.error("Error creating ticket:", error);
            toast.error("Failed to create support ticket");
        } finally {
            setSubmittingTicket(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            open: { bg: 'warning', label: 'Open' },
            in_progress: { bg: 'info', label: 'In Progress' },
            resolved: { bg: 'success', label: 'Resolved' },
            closed: { bg: 'secondary', label: 'Closed' }
        };
        const badge = badges[status] || badges.open;
        return <Badge bg={badge.bg} style={{ borderRadius: "5px" }}>{badge.label}</Badge>;
    };

    const getPriorityBadge = (priority) => {
        const badges = {
            low: { bg: 'secondary', label: 'Low' },
            medium: { bg: 'info', label: 'Medium' },
            high: { bg: 'warning', label: 'High' },
            urgent: { bg: 'danger', label: 'Urgent' }
        };
        const badge = badges[priority] || badges.medium;
        return <Badge bg={badge.bg} style={{ borderRadius: "5px" }}>{badge.label}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    return (
        <>
            <style>
                {`
                    .ant-select-selector {
                        border-radius: 5px !important;
                    }
                    .ant-select-dropdown {
                        border-radius: 5px !important;
                    }
                    .ant-tabs-tab {
                        border-radius: 5px 5px 0 0 !important;
                    }
                    .ant-tabs-content-holder {
                        border-radius: 0 0 5px 5px !important;
                    }
                `}
            </style>
            <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
                <div className="dc-haslayout dc-dbsectionspace">
                    <div className="container-fluid py-3">
                    {/* Header */}
                    <div className="card mb-5" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Help & Support</h5>
                                    <Badge bg="info" style={{ borderRadius: "5px" }}>Support</Badge>
                                </div>
                                {activeTab === "tickets" && (
                                    <Button 
                                        className="btn-primary" 
                                        onClick={() => setShowTicketForm(true)}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <PlusOutlined /> Create Ticket
                                    </Button>
                                )}
                            </div>

                            {/* Tabs */}
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                style={{ borderRadius: "5px" }}
                                items={[
                                    {
                                        key: 'faq',
                                        label: (
                                            <span>
                                                <QuestionCircleOutlined style={{ marginRight: '8px' }} />
                                                FAQ
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'contact',
                                        label: (
                                            <span>
                                                <CustomerServiceOutlined style={{ marginRight: '8px' }} />
                                                Contact Support
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'tickets',
                                        label: (
                                            <span>
                                                <MessageOutlined style={{ marginRight: '8px' }} />
                                                Support Tickets
                                            </span>
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </div>

                    {/* FAQ Tab */}
                    {activeTab === "faq" && (
                        <div className="card" style={{ borderRadius: "5px" }}>
                            <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                                <h5 className="mb-4" style={{ fontWeight: '600', color: '#333' }}>
                                    Frequently Asked Questions
                                </h5>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {faqs.map((faq) => (
                                        <Card 
                                            key={faq.id} 
                                            style={{ 
                                                borderRadius: "5px", 
                                                border: expandedFaq === faq.id ? '2px solid #1890ff' : '1px solid #e5e7eb',
                                                boxShadow: expandedFaq === faq.id ? '0 4px 8px rgba(24,144,255,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                                        >
                                            <Card.Body style={{ padding: '20px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: expandedFaq === faq.id ? '12px' : '0' }}>
                                                            <div style={{ 
                                                                width: '36px', 
                                                                height: '36px', 
                                                                borderRadius: '50%', 
                                                                backgroundColor: '#e3f2fd', 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center',
                                                                flexShrink: 0
                                                            }}>
                                                                <QuestionCircleOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                                            </div>
                                                            <h6 style={{ 
                                                                margin: 0, 
                                                                fontWeight: '600', 
                                                                color: '#333',
                                                                fontSize: '16px',
                                                                flex: 1
                                                            }}>
                                                                {faq.question}
                                                            </h6>
                                                        </div>
                                                        {expandedFaq === faq.id && (
                                                            <div style={{ 
                                                                marginTop: '16px', 
                                                                paddingLeft: '48px',
                                                                paddingTop: '16px',
                                                                borderTop: '1px solid #e5e7eb'
                                                            }}>
                                                                <div style={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    gap: '8px', 
                                                                    marginBottom: '12px' 
                                                                }}>
                                                                    <Badge bg="info" style={{ borderRadius: "5px", fontSize: '11px' }}>
                                                                        {faq.category}
                                                                    </Badge>
                                                                </div>
                                                                <p style={{ 
                                                                    margin: 0, 
                                                                    color: '#666', 
                                                                    fontSize: '15px', 
                                                                    lineHeight: '1.6' 
                                                                }}>
                                                                    {faq.answer}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: '20px', 
                                                        color: '#1890ff',
                                                        transition: 'transform 0.3s ease',
                                                        transform: expandedFaq === faq.id ? 'rotate(180deg)' : 'rotate(0deg)'
                                                    }}>
                                                        <i className="ti-angle-down"></i>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Contact Tab */}
                    {activeTab === "contact" && (
                        <>
                            <div className="card mb-4" style={{ borderRadius: "5px" }}>
                                <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                                    <h5 className="mb-4" style={{ fontWeight: '600', color: '#333' }}>Contact Information</h5>
                                    <div className="row" style={{ rowGap: '20px' }}>
                                        <div className="col-12 col-md-6">
                                            <Card style={{ borderRadius: "5px", border: '1px solid #e5e7eb', height: '100%' }}>
                                                <Card.Body style={{ padding: '24px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                                                        <div style={{ 
                                                            width: '56px', 
                                                            height: '56px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: '#e3f2fd', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}>
                                                            <i className="ti-email" style={{ fontSize: '24px', color: '#1890ff' }}></i>
                                                        </div>
                                                        <div>
                                                            <h6 style={{ margin: 0, fontWeight: '600', color: '#333', fontSize: '16px' }}>Email Support</h6>
                                                            <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>support@insurance.com</p>
                                                        </div>
                                                    </div>
                                                    <p style={{ color: '#999', fontSize: '13px', margin: 0, paddingLeft: '71px' }}>
                                                        We typically respond within 24 hours
                                                    </p>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <Card style={{ borderRadius: "5px", border: '1px solid #e5e7eb', height: '100%' }}>
                                                <Card.Body style={{ padding: '24px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                                                        <div style={{ 
                                                            width: '56px', 
                                                            height: '56px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: '#e8f5e9', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}>
                                                            <i className="ti-mobile" style={{ fontSize: '24px', color: '#4caf50' }}></i>
                                                        </div>
                                                        <div>
                                                            <h6 style={{ margin: 0, fontWeight: '600', color: '#333', fontSize: '16px' }}>Phone Support</h6>
                                                            <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>1-800-INSURANCE</p>
                                                        </div>
                                                    </div>
                                                    <p style={{ color: '#999', fontSize: '13px', margin: 0, paddingLeft: '71px' }}>
                                                        Monday - Friday, 9 AM - 6 PM EST
                                                    </p>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card" style={{ borderRadius: "5px" }}>
                                <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                                    <h5 className="mb-4" style={{ fontWeight: '600', color: '#333' }}>Send us a Message</h5>
                                    <form onSubmit={handleTicketSubmit}>
                                        <div className="row" style={{ rowGap: '20px' }}>
                                            <div className="col-12 col-md-6">
                                                <div className="form-group">
                                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                                        Subject <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Form.Control
                                                        type="text"
                                                        value={ticketForm.subject}
                                                        onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                                                        placeholder="Enter subject"
                                                        required
                                                        style={{ height: '40px', borderRadius: "5px" }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <div className="form-group">
                                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                                        Category
                                                    </label>
                                                    <Select
                                                        value={ticketForm.category}
                                                        onChange={(value) => setTicketForm(prev => ({ ...prev, category: value }))}
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                        options={[
                                                            { value: 'general', label: 'General Inquiry' },
                                                            { value: 'technical', label: 'Technical Support' },
                                                            { value: 'billing', label: 'Billing & Payments' },
                                                            { value: 'claims', label: 'Claims' },
                                                            { value: 'policies', label: 'Policies' },
                                                            { value: 'other', label: 'Other' },
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <div className="form-group">
                                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                                        Priority
                                                    </label>
                                                    <Select
                                                        value={ticketForm.priority}
                                                        onChange={(value) => setTicketForm(prev => ({ ...prev, priority: value }))}
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                        options={[
                                                            { value: 'low', label: 'Low' },
                                                            { value: 'medium', label: 'Medium' },
                                                            { value: 'high', label: 'High' },
                                                            { value: 'urgent', label: 'Urgent' },
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                                        Message <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={6}
                                                        value={ticketForm.message}
                                                        onChange={(e) => setTicketForm(prev => ({ ...prev, message: e.target.value }))}
                                                        placeholder="Enter your message..."
                                                        required
                                                        style={{ borderRadius: "5px", resize: 'vertical' }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <Button 
                                                    type="primary" 
                                                    htmlType="submit"
                                                    loading={submittingTicket}
                                                    style={{ borderRadius: "5px", height: '40px', minWidth: '140px' }}
                                                >
                                                    <MessageOutlined style={{ marginRight: '8px' }} />
                                                    Send Message
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Tickets Tab */}
                    {activeTab === "tickets" && (
                        <div className="card" style={{ borderRadius: "5px" }}>
                            <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <h5 className="mb-0" style={{ fontWeight: '600', color: '#333' }}>Support Tickets</h5>
                                    <Button 
                                        className="btn-outline-primary" 
                                        onClick={fetchTickets}
                                        disabled={loadingTickets}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <ReloadOutlined /> Refresh
                                    </Button>
                                </div>
                                {loadingTickets ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : tickets.length > 0 ? (
                                    <div className="table-responsive">
                                        <table hover size="sm" className="mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Ticket ID</th>
                                                    <th>Subject</th>
                                                    <th>Category</th>
                                                    <th>Priority</th>
                                                    <th>Status</th>
                                                    <th>Created Date</th>
                                                    <th style={{ width: '100px' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tickets.map((ticket) => (
                                                    <tr key={ticket.id}>
                                                        <td data-label="Ticket ID" style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                                                            #{ticket.id}
                                                        </td>
                                                        <td data-label="Subject">
                                                            <div style={{ fontWeight: '500', color: '#333' }}>
                                                                {ticket.subject || "No Subject"}
                                                            </div>
                                                            {ticket.message && (
                                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                    {ticket.message.length > 60 ? `${ticket.message.substring(0, 60)}...` : ticket.message}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td data-label="Category">
                                                            <Badge bg="secondary" style={{ borderRadius: "5px", textTransform: 'capitalize' }}>
                                                                {ticket.category || "General"}
                                                            </Badge>
                                                        </td>
                                                        <td data-label="Priority">
                                                            {getPriorityBadge(ticket.priority)}
                                                        </td>
                                                        <td data-label="Status">
                                                            {getStatusBadge(ticket.status)}
                                                        </td>
                                                        <td data-label="Created Date" style={{ fontSize: '13px', color: '#666' }}>
                                                            {formatDate(ticket.created_at)}
                                                        </td>
                                                        <td data-label="Action">
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-info" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    // Handle view ticket
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                View
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="dc-emptydata-holder">
                                        <div className="dc-emptydata">
                                            <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                                <span></span>
                                                <em>No support tickets found</em>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default HelpSupport;
